const { getElement } = window.BAO.Utils

class PushCartError {
  /**
   * @param {Object} error
   *
   * @returns {void}
   */
  static async handle (error) {
    const instance = new this(error)

    if (instance.genericError) {
      return instance.render(instance.MESSAGES.GENERIC)
    } else if (instance.soldOut) {
      return instance.render(instance.MESSAGES.SOLD_OUT)
    } else if (instance.alreadyHaveAllInCart) {
      return instance.render(instance.MESSAGES.ALREADY_HAVE_ALL_STOCK)
    } else if (instance.addedMoreThanExists) {
      return instance.render(instance.MESSAGES.NOT_ENOUGH_IN_STOCK)
    } else if (instance.cannotFindVariant) {
      return instance.render(instance.MESSAGES.CANNOT_FIND_VARIANT)
    } else {
      return instance.render(instance.MESSAGES.ADD_TOO_MANY)
    }
  }

  constructor (error) {
    this.error = error

    this.MESSAGES = {
      ADD_TOO_MANY: window.pushCartStrings.add_too_many,
      ALREADY_HAVE_ALL_STOCK: window.pushCartStrings.already_have_all_stock,
      CANNOT_FIND_VARIANT: window.pushCartStrings.cannot_find_variant,
      GENERIC: window.pushCartStrings.generic,
      NOT_ENOUGH_IN_STOCK: window.pushCartStrings.not_enough_in_stock,
      SOLD_OUT: window.pushCartStrings.sold_out,
    }

    this.description = error?.description ?? ''
    this.status = error?.status ?? null

    /**
     * @type {PushCart}
     * @public
     */
    this.pushCart = getElement('push-cart')
  }

  render (message) {
    const clone =
      this.pushCart.els['error-template'].element.content.cloneNode(true)
    const text = getElement(this.selectors.text, {
      context: clone,
    })

    text.textContent = message

    this.pushCart.els.errors.element.appendChild(clone)

    return this
  }

  get addedMoreThanExists () {
    return this.description.slice(0, 18) === "You can't add more"
  }

  get alreadyHaveAllInCart () {
    return this.description.slice(0, 4) === 'All '
  }

  get genericError () {
    return this.error?.errors?.toLowerCase() === ' error'
  }

  get soldOut () {
    return this.description.slice(0, 13) === "The product '"
  }

  get cannotFindVariant () {
    return this.description.toLowerCase() === 'cannot find variant'
  }

  get selectors () {
    return {
      item: '[data-push-cart-errors-item]',
      text: '[data-push-cart-errors-text]',
      close: '[data-push-cart-errors-close]',
    }
  }
}

export class PushCart extends window.BAO.CustomElement() {
  static get requiredElements () {
    return ['error-template', 'errors']
  }

  _loading = false

  get loading () {
    return this._loading
  }

  set loading (val) {
    this._loading = val

    this.classList.toggle(this.loadingClass, val)

    const pushCartAside = document.querySelector('[data-push-cart-el="aside"]')

    if (!pushCartAside) return

    pushCartAside.classList.toggle('psh-CartAside-loading', val)
  }

  constructor () {
    super()

    this.sections = window.BAO.sectionRenderer.pushCartSections

    if (this.drawer == null) {
      this.throwError('needs to be used within a <site-drawer> element')
    }
  }

  setupListeners () {
    super.setupListeners()

    this.onBeforeCartAdd = this.onBeforeCartAdd.bind(this)
    this.onAfterCartAdd = this.onAfterCartAdd.bind(this)

    window.BAO.eventBus.addEventListener(
      window.BAO.EVENTS.PUSH_CART.BEFORE_ADD,
      this.onBeforeCartAdd,
    )
    window.BAO.eventBus.addEventListener(
      window.BAO.EVENTS.PUSH_CART.BEFORE_MULTIPLE_ADD,
      this.onBeforeCartAdd,
    )
    window.BAO.eventBus.addEventListener(
      window.BAO.EVENTS.PUSH_CART.BEFORE_ADD_FROM_FORM,
      this.onBeforeCartAdd,
    )

    window.BAO.eventBus.addEventListener(
      window.BAO.EVENTS.PUSH_CART.AFTER_ADD,
      this.onAfterCartAdd,
    )
    window.BAO.eventBus.addEventListener(
      window.BAO.EVENTS.PUSH_CART.AFTER_MULTIPLE_ADD,
      this.onAfterCartAdd,
    )
    window.BAO.eventBus.addEventListener(
      window.BAO.EVENTS.PUSH_CART.AFTER_ADD_FROM_FORM,
      this.onAfterCartAdd,
    )

    window.BAO.eventBus.addEventListener(
      window.BAO.EVENTS.PUSH_CART.CHANGED,
      () => {
        this.waitForGiftItem(() => {
          this.removeGiftItemIfPresent()
        })
        this.refreshNosto()
      },
    )
  }

  disconnectedCallback () {
    super.disconnectedCallback()

    window.BAO.eventBus.removeEventListener(
      window.BAO.EVENTS.PUSH_CART.BEFORE_ADD,
      this.onBeforeCartAdd,
    )

    window.BAO.eventBus.removeEventListener(
      window.BAO.EVENTS.PUSH_CART.AFTER_ADD,
      this.onAfterCartAdd,
    )
  }

  onBeforeCartAdd () {
    window.drawers.activeDrawerKey = 'cart'

    this.loading = true
  }

  onAfterCartAdd (event) {
    if ('sections' in event.detail.state) {
      this.renderSections(event.detail.state.sections)
    }

    this.loading = false
  }

  waitForGiftItem (callback) {
    const observer = new MutationObserver((mutations, obs) => {
      const giftItem = this.querySelector('cart-item[data-gift-check="true"]')
      if (giftItem) {
        obs.disconnect()
        callback()
      }
    })

    observer.observe(this, {
      childList: true,
      subtree: true,
    })
  }

  removeGiftItemIfPresent () {
    const giftItems = this.querySelectorAll(
      'cart-item[data-gift-check="true"]',
    )
    giftItems.forEach((item) => {
      const removeBtn = item.querySelector('[data-cart-item-el="remove"]')
      if (removeBtn) {
        removeBtn.click()
      }
    })
  }

  refreshNosto () {
    // eslint-disable-next-line no-undef
    Nosto.reloadCart()
  }

  /**
   * @param {Response} res
   *
   * @returns {void}
   */
  async handleError (res) {
    this.loading = false

    return PushCartError.handle(await res.json())
  }

  get drawer () {
    return this.closest('[is="site-drawer"]')
  }

  get loadingClass () {
    return this.getAttribute('loading-class')
  }
}

if (!customElements.get('push-cart')) {
  customElements.define('push-cart', PushCart)
}
